import ko from 'knockout';
import _ from 'underscore';
import { helpers, DeterministicView } from '@bingads-webui/deterministic-view';
import { shouldSupportCashbackSettings } from '@bingads-webui-campaign/personalized-offer-shared';
import {
  TargetingLevel,
  TargetingSettingsContext,
  CampaignAdgroupSelectorVnext,
} from '@bingads-webui-campaign/targeting-settings-scaffolds';
import { LocationTargetView } from './location-target-view';
import ViewModel from '../viewmodel/locations-editor';
import Template from '../template/locations-editor.pug';

const { ensureChildView } = helpers;

export class LocationsEditor extends DeterministicView {
  initialize({
    scope,
    level,
    campaignId,
    adgroupId,
    criterions,
    showBidMode,
    adGroup,
    isSmartShoppingCampaign,
    supportCashbackSettings = false,
    currentAdGroup,
    currentCampaign,
    pagePerfMarker,
  }) {
    this.pagePerfMarker = pagePerfMarker;
    this.i18n = TargetingSettingsContext.i18n;
    // hide campaign/adgroup selector when inline editing at adgroup level in adgroup scope
    const hideSelectorControl = _.isUndefined(adGroup);
    this.viewmodel = new ViewModel({
      scope,
      level,
      campaignId,
      adgroupId,
      criterions,
      hideSelectorControl,
    });
    this.supportCashbackSettings = supportCashbackSettings;

    TargetingSettingsContext.eventDispatcher.on('location-targets:modeChanged', (bidMode) => {
      this.viewmodel.showBidMode(bidMode);
    }, this);
    const { IsPersonalizedOffersEnabled } = TargetingSettingsContext.pageContext.Permissions;

    if (this.viewmodel.shouldShowCampaignAdgroupSelector()) {
      const selectedCampaign = adGroup && adGroup.Campaign ? _.extend({
        name: adGroup.Campaign.Name,
        status: adGroup.Campaign.Status,
      }, _.pick(adGroup.Campaign, 'Id', 'Name', 'Status', 'CampaignCashback')) : null;
      const selectedAdGroup = adGroup && adGroup.Campaign ? _.extend({
        name: adGroup.Name,
        status: adGroup.Status,
        campaignId: adGroup.Campaign.Id,
        campaignName: adGroup.Campaign.Name,
      }, _.pick(adGroup, 'Id', 'Name', 'Status', 'AdGroupCashback', 'Campaign')) : null;

      let optionalSelectorParam = {
        optionalCampaignSelect: ['BiddingScheme'],
      };

      if (IsPersonalizedOffersEnabled) {
        optionalSelectorParam = {
          optionalCampaignSelect: ['CampaignCashback', 'BiddingScheme'],
          optionalAdGroupSelect: ['AdGroupCashback'],
          optionalAdGroupExpand: ['CampaignCashback'],
        };
      } else {
        optionalSelectorParam = {
          optionalCampaignSelect: ['BiddingScheme'],
          optionalAdGroupExpand: ['CampaignSettings'],
        };
      }

      let optionalCampaignFilter;
      if (level === TargetingLevel.AdGroup) {
        // adgroup location is not supported for SSC, Hotel, Pmax and LinkedIn
        optionalCampaignFilter = '(CampaignSubType ne Enum.CampaignSubType\'ShoppingSmartAds\') and (CampaignType ne Enum.CampaignType\'Hotel\') and (CampaignType ne Enum.CampaignType\'PerformanceMax\') and (CampaignType ne Enum.CampaignType\'LinkedIn\')';
      } else {
        // campaign location is not supported for Xandr related campaigns
        // LinkedIn campaign didn't support create Location target from Location Gid
        optionalCampaignFilter = '(CampaignSubType ne Enum.CampaignSubType\'BrandAwarenessVideoAds\') and (CampaignSubType ne Enum.CampaignSubType\'DisplayAds\') and (CampaignType ne Enum.CampaignType\'LinkedIn\')';
      }

      ensureChildView(
        this,
        'campaign-adgroup-selector',
        CampaignAdgroupSelectorVnext,
        {
          scope,
          level,
          campaignId,
          i18n: this.i18n,
          selectedCampaign,
          selectedAdGroup,
          optionalCampaignFilter,
          optionalAdgroupFilter: '(Campaign/CampaignType ne Enum.CampaignType\'Hotel\') and (Campaign/CampaignType ne Enum.CampaignType\'PerformanceMax\')',
          ...optionalSelectorParam,
        },
        {
          campaignSelected: 'onCampaignSelected',
          adgroupSelected: 'onAdgroupSelected',
        }
      );
      if (selectedAdGroup && level === TargetingLevel.AdGroup) {
        this.onAdgroupSelected(selectedAdGroup);
      } else if (selectedCampaign && level === TargetingLevel.Campaign) {
        this.onCampaignSelected(selectedCampaign);
      }
    } else {
      this.setState({
        level,
        campaignId,
        adgroupId,
        criterions,
        showBidMode,
        isSmartShoppingCampaign,
        supportCashbackSettings,
        currentAdGroup,
        currentCampaign,
      });
    }
  }

  get events() {
    return {
      'click .btn-save': 'save',
      'click .btn-cancel': 'cancel',
    };
  }

  get template() {
    return Template;
  }

  renderTrunk() {
    ko.cleanNode(this.el);
    super.renderTrunk();
    this.delegateEvents();
    ko.applyBindings(this.viewmodel, this.el);
    return this;
  }

  save() {
    if (!this.locationTargetView || !this.locationTargetView.validate()) {
      return;
    }

    const highLatencyThreshold = 3000;
    if (this.locationTargetView.updatedCriterionsCount() > highLatencyThreshold) {
      this.viewmodel.shouldShowHighLantencyNote(true);
    }

    const response = this.locationTargetView.save();
    if (response) {
      response.then((triggerClose) => {
        if (triggerClose !== false && !this.locationTargetView.hasErrors()) {
          this.trigger('close', true);
        }
      });
    }
  }

  cancel() {
    this.trigger('close');
  }

  setState({
    level,
    campaignId,
    adgroupId,
    criterions,
    showBidMode,
    isSmartShoppingCampaign,
    supportCashbackSettings,
    currentCampaign,
    currentAdGroup,
  }) {
    // Split location editor marker into two marker
    // Because we may need wait user to select campaign or adgroup
    // This marker is used to record data fetch and render latency
    if (this.pagePerfMarker) {
      this.perfMarker = this.pagePerfMarker.createChild('Locations_Editor_Render', { autoEnd: true });
      this.perfMarker.willInit();
    }
    this.viewmodel.selectedCampaign(campaignId);
    this.viewmodel.selectedAdgroup(adgroupId);
    this.showLocationTargetView({
      level,
      campaignId,
      adgroupId,
      criterions,
      showBidMode,
      isSmartShoppingCampaign,
      supportCashbackSettings,
      currentCampaign,
      currentAdGroup,
    });
  }

  showLocationTargetView({
    level,
    campaignId,
    adgroupId,
    criterions,
    showBidMode,
    isSmartShoppingCampaign,
    currentCampaign,
    currentAdGroup,
  }) {
    if (this.getChild('location-target-view')) {
      this.detachChild('location-target-view').remove();
    }
    this.locationTargetView = new LocationTargetView({
      level,
      campaignId,
      adgroupId,
      criterions,
      showBidMode,
      isSmartShoppingCampaign,
      supportCashbackSettings: this.supportCashbackSettings,
      currentCampaign,
      currentAdGroup,
      perfMarker: this.perfMarker,
    });
    if (isSmartShoppingCampaign) {
      this.viewmodel.showBidMode(true);
    }
    this.locationTargetView.on('closeLocationEditor', () => this.trigger('close', true));
    this.setChild('location-target-view', this.locationTargetView);
    const forceRenderFlag = this.model.get('forceRenderFlag');
    return super.setState({
      level,
      campaignId,
      adgroupId,
      forceRenderFlag: !forceRenderFlag,
    });
  }

  onCampaignSelected(campaign) {
    this.supportCashbackSettings = shouldSupportCashbackSettings(
      campaign,
      TargetingSettingsContext.pageContext.Permissions
    );

    this.setState({
      level: this.viewmodel.level,
      campaignId: campaign.Id,
      criterions: this.viewmodel.criterions,
      showBidMode: this.locationTargetView ?
        this.locationTargetView.locationTargets.viewmodel.bidMode() :
        this.options.showBidMode,
      isSmartShoppingCampaign: campaign.CampaignSubType === 'ShoppingSmartAds' || campaign.CampaignSubType === 12,
      currentCampaign: campaign,
    });
    this.viewmodel.criterions = undefined;
  }

  onAdgroupSelected(adgroup) {
    // mock
    // adgroup.Campaign.CashbackSettings = { CashbackMonthlyBudget: 1 }; // eslint-disable-line
    const supportCashbackSettings = shouldSupportCashbackSettings(
      adgroup.Campaign,
      TargetingSettingsContext.pageContext.Permissions
    );
    this.supportCashbackSettings = supportCashbackSettings;

    this.setState({
      level: this.viewmodel.level,
      campaignId: adgroup.campaignId,
      adgroupId: adgroup.Id,
      criterions: this.viewmodel.criterions,
      showBidMode: this.locationTargetView ?
        this.locationTargetView.locationTargets.viewmodel.bidMode() :
        this.options.showBidMode,
      currentCampaign: adgroup.Campaign,
      currentAdGroup: adgroup,
    });
    this.viewmodel.criterions = undefined;
  }

  remove() {
    TargetingSettingsContext.eventDispatcher.off(null, null, this);
    super.remove();
  }
}
